import React, { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import SharedDataContext from "../../../SharedDataContext";
import { getTeamFeatureAccess, updateTeamFeatureAccess } from "../../../apis/workspace/teams";
import TableLoader from "../../Loaders/TableLoader";
import { Switch } from "../../ui/switch";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function FeatureAccess() {
  const captureEvent = useCaptureEvent();
  const { teamId } = useParams();
  const queryClient = useQueryClient();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const { data: features = [], isLoading } = useQuery(
    ["team_feature_access", teamId, activeWorkspace?.id],
    () =>
      getTeamFeatureAccess({
        teamId: teamId,
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: Boolean(activeWorkspace?.id) && !!teamId,
      select: (response) => response?.data ?? [],
      staleTime: 300_000,
    }
  );

  const updateTeamFeatureAccessFn = useMutation("update_team_feature_access", updateTeamFeatureAccess, {
    onSuccess(data) {
      toast.success(data?.message);
      queryClient.invalidateQueries("team_feature_access");
    },
  });

  const toggleAccess = (feature, enabled) => {
    if (updateTeamFeatureAccessFn.isLoading) {
      return;
    }
    updateTeamFeatureAccessFn.mutate({
      teamId: teamId,
      feature: feature,
      data: { enabled: enabled },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  if (isLoading) {
    return <TableLoader />;
  }

  return (
    <div>
      {features && features.length > 0 && (
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="w-8/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Name
                      </th>
                      <th scope="col" className="w-4/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Toggle access
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {features.map((item) => (
                      <tr key={item?.feature}>
                        <td className="w-8/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          {item?.label}
                        </td>
                        <td className="w-4/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          <div className="inline-flex items-center">
                            <Switch
                              checked={item?.enabled}
                              onCheckedChange={(checked) => {
                                toggleAccess(item?.feature, checked);
                                captureEvent("SET_TeamDetails_ToggleFeatureAccess", {
                                  team_id: teamId,
                                  feature: item?.feature,
                                  enabled: checked,
                                });
                              }}
                            />
                            <span className="ml-3">{item?.enabled ? "Enabled" : "Disabled"}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
